import type { MajorStatus } from "@citrine/types/status";

type Status = "Pending" | "Submitted" | "Running" | "Success" | "Failure";

interface JobTask {
  id: string;
  task_type: string;
  status: Status;
  dependencies: string[];
  failure_reason?: string;
}

export type JobOutput = Record<string, string>;

export interface JobExecutionStatus<Output extends JobOutput = JobOutput> {
  job_type: string;
  status: Status;
  tasks: JobTask[];
  // if the output type allows any string, then it wasn't narrowed and we should treat it as unknown
  output: string extends keyof Output ? unknown : Output;
}

export class JobExecutionError<
  Output extends JobOutput = JobOutput,
> extends Error {
  constructor(
    public readonly job: JobExecutionStatus<Output>,
    message: string = "Job Execution Failed"
  ) {
    super(message);
  }
}
export class JobTimeoutError<
  Output extends JobOutput = JobOutput,
> extends JobExecutionError<Output> {
  constructor(
    job: JobExecutionStatus<Output>,
    message: string = "Job Execution Timeout"
  ) {
    super(job, message);
  }
}

export interface PollingOptions {
  interval?: number;
  timeout?: number;
}

export function isJobFinished<Output extends JobOutput = JobOutput>(
  jobExecutionStatus?: JobExecutionStatus<Output>
) {
  return (
    jobExecutionStatus?.status === "Success" ||
    jobExecutionStatus?.status === "Failure"
  );
}

export function isJobInProgress<Output extends JobOutput = JobOutput>(
  jobExecutionStatus?: JobExecutionStatus<Output>
) {
  return (
    jobExecutionStatus?.status === "Pending" ||
    jobExecutionStatus?.status === "Running"
  );
}

export function jobExecutionStatusToProgressStatus(
  status?: JobExecutionStatus["status"]
): MajorStatus | undefined {
  switch (status) {
    case "Failure":
      return "FAILED";
    case "Pending":
    case "Submitted":
    case "Running":
      return "INPROGRESS";
    case "Success":
      return "SUCCEEDED";
  }
}
